/** Zustand eines Eintrags, nach dem `GET …/entries?state=` filtert (`EntryState` im BE, NEX-1724). */
export const ENTRY_STATES = ['FRAGILE', 'UNUSED', 'NEVER_HIT', 'MISSING'] as const;
export type EntryState = (typeof ENTRY_STATES)[number];

/** Art des Kandidaten, ueber den der Runner den Eintrag zuletzt gefunden hat. */
export type HitKind = 'TESTID' | 'ID' | 'ROLE' | 'NAME' | 'LABEL' | 'TEXT' | 'CSS' | 'XPATH' | 'POSITION';

/**
 * Eine Objektbibliothek mit ihrer Eintragszahl (`ObjectMappingSummary` im BE). Die Zaehler je
 * Zustand liefert das BE immer; optional nur, damit aeltere Antworten nicht brechen.
 */
export interface ObjectMappingSummary {
  id: string;
  name: string;
  engineId: string;
  description: string | null;
  entryCount: number;
  fragileCount?: number;
  unusedCount?: number;
  neverHitCount?: number;
  missingCount?: number;
}

/** Body von `POST …/object-mappings`; ohne `engineId` legt das BE die Bibliothek fuer `browser` an. */
export interface CreateObjectMappingRequest {
  name: string;
  engineId?: string;
  description?: string;
}

/** Typen, die das BE fuer neue Eintraege annimmt (`ObjectTypes` im BE). */
export const OBJECT_TYPES = ['Button', 'Eingabe', 'Auswahl', 'Link', 'Tabelle', 'Bereich', 'Seite'] as const;

/** Kandidaten-Arten, die das BE annimmt (`LocatorRules.STRATEGIES`). */
export const LOCATOR_STRATEGIES = ['css', 'xpath', 'role', 'text', 'label'] as const;

/** Ein Kandidat im Schreib-Body; `name` nur bei `role`. */
export interface LocatorInput {
  strategy: string;
  value: string;
  name?: string;
}

/** Body von `POST …/entries`. */
export interface CreateEntryRequest {
  logicalName: string;
  typeName?: string;
  url?: string;
  locators?: LocatorInput[];
}

/** Body von `PUT …/entries/{id}`: Typ, Adresse und Kandidaten werden als Ganzes ersetzt. */
export interface UpdateEntryRequest {
  typeName?: string;
  url?: string;
  locators?: LocatorInput[];
}

/** Antwort von `GET …/object-mappings/search`: Namen, die mit dem Suchtext anfangen. */
export interface ObjectMappingSearchResult {
  mappings: { id: string; name: string; engineId: string }[];
  entries: { id: string; mappingId: string; mappingName: string; logicalName: string; reference: string }[];
}

/** Ein Locator-Kandidat, z. B. `{ strategy: 'css', value: '#login' }`. */
export type ObjectMappingLocator = Record<string, string>;

/** Ein logisches Objekt einer Bibliothek (`ObjectMappingEntryView` im BE). */
export interface ObjectMappingEntryView {
  id: string;
  logicalName: string;
  typeName: string | null;
  fulltag: string | null;
  locators: ObjectMappingLocator[];
  url: string | null;
  missingSince?: string | null;
  lastHitAt?: string | null;
  /** 0-basiert. */
  lastHitIndex?: number | null;
  lastHitKind?: HitKind | null;
  usageCount?: number;
}

/** Wo im Schritt ein Objektverweis steht (`ObjectRefSource` im BE). */
export type ObjectRefSource = 'TCDL_TARGET' | 'TCDL_CONDITION' | 'STEP_CONDITION';

/** Eine Stelle im Schritt; `lineNumber` und `text` fehlen bei der Bedingung am Schritt. */
export interface UsageLine {
  lineNumber: number | null;
  source: ObjectRefSource;
  reference: string;
  text: string | null;
  /** Zeile nach dem Umbenennen, nur in der Vorschau und im Ergebnis (NEX-1278). */
  newText?: string | null;
}

export interface UsageStep {
  stepId: string;
  stepKey: string;
  stepTitle: string;
  lines: UsageLine[];
}

/** Ein Testfall oder Testmodul mit den betroffenen Schritten. */
export interface UsageOwner {
  id: string;
  key: string;
  title: string;
  steps: UsageStep[];
}

/** Wer ein Objekt benutzt (NEX-1277). */
export interface ObjectUsages {
  occurrenceCount: number;
  testCases: UsageOwner[];
  modules: UsageOwner[];
  unlinkedSteps: UsageStep[];
  /** Nur beim Eintrag, und nur wenn andere Bibliotheken denselben Namen tragen. */
  duplicates?: ObjectDuplicates | null;
}

/**
 * Gleicher Objektname in mehreren Bibliotheken (NEX-1278). Die Kurzform ohne Bibliothek loest auf
 * hoechstens einen davon auf; nur auf ihn beziehen sich Verwender und Umbenennen der Kurzform.
 */
export interface ObjectDuplicates {
  otherMappings: string[];
  assigned: boolean;
  /** Bibliothek, auf deren Eintrag die Kurzform aufloest; null, wenn sie nicht eindeutig ist. */
  resolvedMapping: string | null;
}

/** Vorschau oder Ergebnis einer Umbenennung (NEX-1278), beides in derselben Form. */
export interface RenameResult {
  oldName: string;
  newName: string;
  applied: boolean;
  occurrenceCount: number;
  testCaseCount: number;
  moduleCount: number;
  usages: ObjectUsages;
  notTouched: UsageLine[];
}
